import * as moment from "moment"
import PptxGenJS, * as PptxGenJSTypes from "pptxgenjs"
import * as React from "react"
import * as ReactDOM from "react-dom"
import * as Utils from "./utils"
import PpiGeneralTable, { Data as PpiGeneralData } from "./ppi-general-table"
import PpiProjectTable from "./ppi-project-table"
import PpiThemeTable from "./ppi-theme-table"
import * as ChartsConfiguration from "../pptx-configuration/charts"
import * as Constants from "../pptx-configuration/constants"
import { MasterSlide } from "../pptx-configuration/master-slides"
import * as PlaceholdersConfiguration from "../pptx-configuration/placeholders"
import { getHtmlToPowerPointBaseOptions } from "../pptx-configuration/slides"
import { DocumentSection } from "../types"
import * as CommonUtils from "../utils"
import * as Conf from "../../configuration"
import * as CounterDomain from "../../../../domain/sfp/counter-domain"
import * as PpiDomain from "../../../../domain/sfp/ppi-domain"
import * as Model from "../../../../model"

const RATIO_BODY_TOP_OFFSET = CommonUtils.toInches(2)

export function checkPpiSection(
	configuration: Conf.ReportConfiguration
): boolean {
	const ppi = configuration.ppi
	return ppi.generalTable
		|| !!(ppi.expenseThemes && ppi.expenseThemes.length)
		|| !!(ppi.projects && ppi.projects.length)
}

export function createPpiSection(
	pptx: PptxGenJS,
	configuration: Conf.ReportConfiguration,
	numbering: number[],
	localAuthority: Model.LocalAuthority,
	ppi: Model.Ppi,
	decomposedLoans: Model.LoanWithDecomposition[],
	scenario: Model.Scenario
): DocumentSection {
	if (!checkPpiSection(configuration)) return { slides: [], title: null }

	const title = CommonUtils.formatTitle(
		"Plan Pluriannuel d'Investissement", numbering)
	const slides: PptxGenJSTypes.Slide[] = []
	const summarySlide = pptx.addNewSlide(MasterSlide.Default)
	summarySlide.addText(title, {
		placeholder: PlaceholdersConfiguration.Placeholder.Title
	})
	slides.push(summarySlide)

	const ppiTitles: string[] = []
	const general = configuration.ppi.generalTable
	const ppiYearLoans = PpiDomain.getPpiYearLoans(decomposedLoans, ppi)
	const ppiYears = PpiDomain.filterPpiYearsByYears(
		ppi.years,
		configuration.ppi.years
	)
	const startYear = ppiYears[0].year
	let partNumber = 0
	if (general) {
		const title = CommonUtils.formatTitle(
			"PPI général", numbering.concat(++partNumber))
		let title1 = CommonUtils.formatTitle('Recettes d\'investissement', numbering.concat(1));
		let title2 = CommonUtils.formatTitle('Dépenses d\'investissement', numbering.concat(2));
		let title3 = CommonUtils.formatTitle('Ratios et Résultat Annuel PPI', numbering.concat(3));
		let title4 = CommonUtils.formatTitle(
			'Résultat Annuel PPI',
			numbering.concat(4)
		);
		let priorities: number[] = configuration.ppi.priorities
		const ppiRecettesData = Utils.createPpiRecettesData(
			localAuthority,
			ppiYears,
			ppi,
			ppiYearLoans,
			scenario,
			startYear,
			priorities
		)
		const ppiDispenseslData = Utils.createPpiDispensesData(
			localAuthority,
			ppiYears,
			ppi,
			ppiYearLoans,
			scenario,
			startYear,
			priorities
		)
		const ppiRatioData = Utils.createPpiRatioData(
			localAuthority,
			ppiYears,
			ppi,
			ppiYearLoans,
			scenario,
			startYear,
			priorities
		)
		const ppiResultantData = Utils.createPpiResultantData(
			localAuthority,
			ppiYears,
			ppi,
			ppiYearLoans,
			scenario,
			startYear,
			priorities
		)
		const ppiRatioAndResultData = {...ppiRatioData, ...ppiResultantData}

		addPpiGeneralSlides(pptx, title1, ppiRecettesData, ppiYears, configuration);
    addPpiGeneralSlides(
      pptx,
      title2,
      ppiDispenseslData,
      ppiYears,
      configuration
    );
    addPpiGeneralSlides(pptx, title3, ppiRatioAndResultData, ppiYears, configuration);
    ppiTitles.push(title)
	}

	const themes = configuration.ppi.expenseThemes

	if (themes && themes.length) {
		const title = CommonUtils.formatTitle(
			"Dépenses", numbering.concat(++partNumber))
		ppiTitles.push(title)

		for (const theme of themes) {
			addPpiThemeSlides(
				pptx, title, theme, ppi, ppiYears, startYear, configuration)
		}
	}

	const projects = configuration.ppi.projects

	if (projects && projects.length) {
		const title = CommonUtils.formatTitle(
			"Fiches projets", numbering.concat(++partNumber))
		ppiTitles.push(title)

		for (const project of projects) {
			addPpiProjectSlides(pptx, title, project, ppi, ppiYears, configuration)
		}
	}

	return { slides, title }
}

function addPpiGeneralSlides(
	pptx: PptxGenJS,
	title: string,
	ppiGeneralData: any,
	ppiYears: Model.PpiYear[],
	conf: Conf.ReportConfiguration
): void {
	const tableId = "management-report-prospective-ppi-general"
	const table = <PpiGeneralTable
		currency={conf.currency}
		data={ppiGeneralData}
		id={tableId}
		labelColumnWidth={CommonUtils.toInches(6.5)}
		locale={conf.locale}
		ppiYears={ppiYears}
		roundness={conf.generalities.roundness}
		enableHideZeroLines={conf.generalities.enableHideZeroLines}
		tableConfiguration={conf.table}
		width={Constants.bodyW} />

	const tempDiv = document.createElement("div")
	document.head.appendChild(tempDiv)

	ReactDOM.render(table, tempDiv, () => {
		const options = getHtmlToPowerPointBaseOptions(title)
		pptx.addSlidesForTable(tableId, options)
		document.head.removeChild(tempDiv)
	})
}

function addPpiProjectSlides(
	pptx: PptxGenJS,
	title: string,
	project: Model.PpiInvestmentProject,
	ppi: Model.Ppi,
	ppiYears: Model.PpiYear[],
	conf: Conf.ReportConfiguration
): void {
	const theme = PpiDomain.findThemeFromProject(ppi.investment_themes, project)
	const parent = PpiDomain.findParentTheme(theme, ppi.investment_themes)
	const parentName = parent && parent.name ? `${parent.name} > ` : ""
	const subtitle: PptxGenJSTypes.TextWithOptions[] = [
		{
			text: title,
			options: {
				...Constants.defaultTitleOptions,
				breakLine: true
			}
		},
		{
			text: `${parentName}${theme.name} > ${project.name}`,
			options: { ...Constants.defaultSubtitleOptions }
		}
	]
	const tableId = `management-report-prospective-ppi-project-${project.id}`
	const table = <PpiProjectTable
		columnLabelWidth={CommonUtils.toInches(6)}
		currency={conf.currency}
		id={tableId}
		locale={conf.locale}
		ppi={ppi}
		ppiYears={ppiYears}
		project={project}
		roundness={conf.generalities.roundness}
		enableHideZeroLines={conf.generalities.enableHideZeroLines}
		tableConfiguration={conf.table} />

	const tempDiv = document.createElement("div")
	document.head.appendChild(tempDiv)

	ReactDOM.render(table, tempDiv, () => {
		const options = getHtmlToPowerPointBaseOptions(subtitle)
		pptx.addSlidesForTable(tableId, options)
		document.head.removeChild(tempDiv)
	})
}

function addPpiThemeSlides(
	pptx: PptxGenJS,
	title: string,
	theme: Model.PpiInvestmentTheme,
	ppi: Model.Ppi,
	ppiYears: Model.PpiYear[],
	startYear: number,
	conf: Conf.ReportConfiguration
): void {
	const parent = PpiDomain.findParentTheme(theme, ppi.investment_themes)
	const parentName = parent && parent.name ? `${parent.name} > ` : ""
	const subtitle: PptxGenJSTypes.TextWithOptions[] = [
		{
			text: title,
			options: {
				...Constants.defaultTitleOptions,
				breakLine: true
			}
		},
		{
			text: `${parentName}${theme.name}`,
			options: { ...Constants.defaultSubtitleOptions }
		}
	]
	const tableId = `management-report-prospective-ppi-theme-${theme.id}`
	const table = <PpiThemeTable
		currency={conf.currency}
		id={tableId}
		fctvaColumnWidth={CommonUtils.toInches(1.2)}
		labelColumnWidth={CommonUtils.toInches(6)}
		locale={conf.locale}
		ppi={ppi}
		ppiYears={ppiYears}
		priorityColumnWidth={CommonUtils.toInches(1.3)}
		roundness={conf.generalities.roundness}
		enableHideZeroLines={conf.generalities.enableHideZeroLines}
		startYear={startYear}
		tableConfiguration={conf.table}
		theme={theme}
		width={Constants.bodyW} />

	const tempDiv = document.createElement("div")
	document.head.appendChild(tempDiv)

	ReactDOM.render(table, tempDiv, () => {
		const options = getHtmlToPowerPointBaseOptions(subtitle)

		pptx.addSlidesForTable(tableId, options)
		document.head.removeChild(tempDiv)
	})
}

function createRatioServiceSlide(
	pptx: PptxGenJS,
	title: string,
	service: Model.Service,
	records: Model.SituationRecord[],
	scenario: Model.Scenario,
	addTable: boolean,
	addChart: boolean,
	tableConf: Conf.TableConfiguration
): PptxGenJSTypes.Slide {
	const slide = pptx.addNewSlide(MasterSlide.Chart)
	slide.addText(title, {
		placeholder: PlaceholdersConfiguration.Placeholder.Title
	})

	const nowMoment = moment()
	const dayOfYear = nowMoment.dayOfYear()
	const daysInYear = nowMoment.isLeapYear() ? 366 : 365

	slide.addText(
		getRatioBodyText(service, records, scenario, dayOfYear, daysInYear),
		{
			//fontFace: Constants.DEFAULT_FONT_FACE,
			placeholder: PlaceholdersConfiguration.Placeholder.Body,
            ...Constants.getTextPlacementOptions
		})

	const twoColumns = addTable && addChart

	if (addTable) {
		const data = Utils.createRatioTableData(
			service, records, scenario, dayOfYear, daysInYear, tableConf)

		const options = getRatioTableOptions(twoColumns)
		slide.addTable(data, options)
	}

	if (addChart) {
		const data = Utils.createRatioChartData(
			service, records, scenario, dayOfYear, daysInYear)

		const options = getRatioChartOptions(twoColumns)
		slide.addChart(pptx.charts.BAR, data, options)
	}

	return slide
}

function getRatioBodyText(
	service: Model.Service,
	records: Model.SituationRecord[],
	scenario: Model.Scenario,
	dayOfYear: number,
	daysInYear: number
): { text: string, options?: PptxGenJSTypes.TextOptions }[] {
	const { engaged, liquidated } = CounterDomain.sumBudgets(records)
	const planned = service.plannedConsumption || 1
	const totalExpense = CounterDomain.getTotalExpense(engaged, liquidated)
	const annualBudget = CounterDomain.getAnnualBudget(service, scenario)
	const repository = CounterDomain.getRepository(
		annualBudget,
		planned,
		dayOfYear,
		daysInYear)
	const percentageConsumption = CounterDomain.getPercentageConsumption(
		totalExpense,
		CounterDomain.getRepository(annualBudget, planned, dayOfYear, daysInYear))
	const absoluteConsumption = CounterDomain.getAbsoluteConsumption(
		totalExpense,
		repository)
	const percentFormatter = new Intl.NumberFormat(
		navigator.language,
		{ style: "percent" })
	const euroFormatter = new Intl.NumberFormat(
		navigator.language,
		{ currency: "EUR", style: "currency" })

	let color = "eda631"
	let mood = "😐"

	switch (CounterDomain.getLevelOfConsumption(percentageConsumption)) {
		case CounterDomain.LevelOfConsumption.High:
			color = "ed3a31"
			mood = "🙁"
			break
		case CounterDomain.LevelOfConsumption.Low:
			color = "31ed53"
			mood = "🙂"
			break
	}
	let consumptionLabel = absoluteConsumption < 0
		? "une économie"
		: "un dépassement"

	return [
		{
			text: `Gestionnaire : ${service.label}`,
			options: { breakLine: true }
		},
		{ text: mood, options: { breakLine: false, color, fontSize: 12 } },
		{
			text: ` Pourcentage de consommation : ${percentFormatter.format(
				percentageConsumption)}`,
			options: { bold: true, breakLine: false, fontSize: 12 }
		},
		{
			text: ` (soit ${consumptionLabel} de ${euroFormatter.format(Math.abs(
				absoluteConsumption))} )`,
			options: { breakLine: true, fontSize: 12 }
		}
	]
}

function getRatioChartOptions(
	twoColumns: boolean
): PptxGenJSTypes.ChartOptions {
	const placeholderOptions = PlaceholdersConfiguration
		.getPlaceholderChartBodyOptions()

	const options = twoColumns
		? ChartsConfiguration.getChartSecondOfTwoOptions(placeholderOptions)
		: ChartsConfiguration.getChartBaseOptions(placeholderOptions)

	options.h = (options.h as number) - RATIO_BODY_TOP_OFFSET
	options.showLegend = false
	options.showTitle = false
	options.y = (options.y as number) + RATIO_BODY_TOP_OFFSET

	return options
}

function getRatioTableOptions(
	twoColumns: boolean
): PptxGenJSTypes.TableOptions {
	const w = twoColumns ? Constants.bodyW / 2 : Constants.bodyW
	const x = Constants.margins.left
	const y = Constants.bodyTop + RATIO_BODY_TOP_OFFSET

	return { w, x, y }
}



// WEBPACK FOOTER //
// ./src/component/management-report/generator/ppi-generator/index.tsx